import {useQuery, useInfiniteQuery, useQueryClient, keepPreviousData} from '@tanstack/react-query';
import {useEffect, useRef} from 'react';
import {ReportType} from '../types/ReportType';
import {ProductionDropdownType} from '../types/ProductionType';
import {PersistentIncidentType} from '../types/IncidentType';
import {ReportNoteSetting} from '../types/ReportNoteSetting';
import {ReportTypes} from '../types/enums/ReportTypes';
import {ReportsFilterAndSort} from '../redux/slice/myReportsSlice';

// =============================================================================
// Query Key Factory
// =============================================================================

export const reportKeys = {
    all: ['reports'] as const,
    detail: (reportId: number) => [...reportKeys.all, 'detail', reportId] as const,
    byProductionAndType: (productionId: number, reportType: string) =>
        [...reportKeys.all, 'byProduction', productionId, reportType] as const,
    myReports: (filters: MyReportsFilters) => [...reportKeys.all, 'my', filters] as const,
    myReportIds: (filters: ReportsFilterAndSort) => [...reportKeys.all, 'my-ids', filters] as const,
};

export const productionKeys = {
    all: ['productions'] as const,
    search: (seasonId: number, categoryId: number, matchDay: number) =>
        [...productionKeys.all, 'search', {seasonId, categoryId, matchDay}] as const,
};

export const incidentKeys = {
    all: ['incidents'] as const,
    severe: (productionId: number) => [...incidentKeys.all, 'severe', productionId] as const,
};

export const settingsKeys = {
    all: ['settings'] as const,
    reportNote: (reportType: string) => [...settingsKeys.all, 'reportNote', reportType] as const,
};

// =============================================================================
// Types
// =============================================================================

export interface MyReportsFilters {
    categoryId?: number;
    sortBy?: 'matchDay' | 'airDate';
    sortDesc?: boolean;
}

interface ProductionsSearchResult {
    productions: ProductionDropdownType[];
    cstProductions: ProductionDropdownType[];
    raw: ProductionDropdownType[];
}

interface MyReportsResponse {
    result: ReportType[];
    total: number;
}

// =============================================================================
// Fetch Functions
// =============================================================================

const fetchReportById = async (reportId: number): Promise<ReportType> => {
    const response = await fetch(
        `${window.environment.backend}api/1.0/reports/${reportId}`,
        {credentials: 'include'}
    );

    if (!response.ok) {
        throw new Error(`Failed to fetch report: ${response.status}`);
    }

    const data = await response.json();
    return data.result;
};

// Exported for prefetching
export const fetchReportByProductionAndType = async (
    productionId: number,
    reportType: string
): Promise<ReportType> => {
    const response = await fetch(
        `${window.environment.backend}api/1.0/reports/production/${productionId}/report-type/${reportType}`,
        {credentials: 'include'}
    );

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const error = new Error(`Failed to fetch report: ${response.status}`);
        (error as Error & {errorCode?: number}).errorCode = errorData.errorResponse?.errorCode;
        throw error;
    }

    const data = await response.json();
    return data.result;
};

const fetchProductions = async (
    seasonId: number,
    categoryId: number,
    matchDay: number
): Promise<ProductionDropdownType[]> => {
    const response = await fetch(
        `${window.environment.backend}api/1.0/productions/search?seasonId=${seasonId}&categoryId=${categoryId}&matchDay=${matchDay}`,
        {credentials: 'include'}
    );

    if (!response.ok) {
        throw new Error(`Failed to fetch productions: ${response.status}`);
    }

    const data = await response.json();
    return data.result ?? [];
};

const fetchSevereIncidents = async (productionId: number): Promise<PersistentIncidentType[]> => {
    const response = await fetch(
        `${window.environment.backend}api/1.0/incidents/severe?productionId=${productionId}`,
        {credentials: 'include'}
    );

    if (!response.ok) {
        throw new Error(`Failed to fetch severe incidents: ${response.status}`);
    }

    const data = await response.json();
    return data.result ?? [];
};

const fetchReportNoteSettings = async (reportType: string): Promise<ReportNoteSetting | null> => {
    const response = await fetch(
        `${window.environment.backend}api/1.0/reportsettings/notes/${reportType}`,
        {credentials: 'include'}
    );

    if (!response.ok) {
        throw new Error(`Failed to fetch report note settings: ${response.status}`);
    }

    const data = await response.json();
    return data.result;
};

const fetchMyReports = async (
    filters: MyReportsFilters,
    page: number
): Promise<MyReportsResponse> => {
    const searchParams = new URLSearchParams();

    if (filters.categoryId) {
        searchParams.append('categoryId', String(filters.categoryId));
    }

    if (filters.sortBy) {
        searchParams.append('sortBy', filters.sortBy);
    }

    if (filters.sortDesc !== undefined) {
        searchParams.append('sortDesc', String(filters.sortDesc));
    }

    searchParams.append('page', String(page));

    const response = await fetch(
        `${window.environment.backend}api/1.0/my-reports?${searchParams.toString()}`,
        {credentials: 'include'}
    );

    if (!response.ok) {
        throw new Error(`Failed to fetch my reports: ${response.status}`);
    }

    const data = await response.json();
    return {
        result: data.result ?? [],
        total: data.total ?? 0
    };
};

const fetchMyReportIds = async (filters: ReportsFilterAndSort): Promise<number[]> => {
    const searchParams = new URLSearchParams();

    if (filters.category?.id) {
        searchParams.append('categoryId', String(filters.category.id));
    }

    if (filters.season?.id) {
        searchParams.append('seasonId', String(filters.season.id));
    }

    if (filters.matchDay) {
        searchParams.append('matchDay', String(filters.matchDay));
    }

    if (filters.sortBy) {
        searchParams.append('sortBy', filters.sortBy);
    }

    if (filters.desc !== undefined) {
        searchParams.append('sortDesc', String(filters.desc));
    }

    const response = await fetch(
        `${window.environment.backend}api/1.0/my-reports/ids?${searchParams.toString()}`,
        {credentials: 'include'}
    );

    if (!response.ok) {
        return [];
    }

    const data = await response.json();
    return data.result ?? [];
};

// =============================================================================
// Query Hooks
// =============================================================================

/**
 * Fetch a report by its ID
 * Used when navigating to /reports/:reportId
 *
 * keepPreviousData ensures that when switching between reports,
 * the previous report remains visible until the new one is loaded.
 * This prevents a blank screen during loading.
 */
export const useReportById = (reportId: number | undefined) => {
    return useQuery({
        queryKey: reportKeys.detail(reportId!),
        queryFn: () => fetchReportById(reportId!),
        enabled: !!reportId,
        staleTime: 5 * 60 * 1000, // 5 minutes
        placeholderData: keepPreviousData,
    });
};

/**
 * Fetch a report by production ID and report type
 * Used when selecting filters in ReportSearch
 */
export const useReportByProductionAndType = (
    productionId: number | undefined,
    reportType: string | undefined,
    enabled: boolean = true
) => {
    return useQuery({
        queryKey: reportKeys.byProductionAndType(productionId!, reportType!),
        queryFn: () => fetchReportByProductionAndType(productionId!, reportType!),
        enabled: enabled && !!productionId && !!reportType,
        staleTime: 5 * 60 * 1000, // 5 minutes
        retry: (failureCount, error) => {
            // Don't retry on 400 errors (report doesn't exist for this combination)
            if (error instanceof Error && error.message.includes('400')) {
                return false;
            }
            return failureCount < 1;
        },
    });
};

/**
 * Search productions by season, category, and match day
 * Used in ReportSearch to populate the production dropdown
 */
export const useProductionsSearch = (
    seasonId: number | undefined,
    categoryId: number | undefined,
    matchDay: number | undefined
) => {
    return useQuery({
        queryKey: productionKeys.search(seasonId!, categoryId!, matchDay!),
        queryFn: () => fetchProductions(seasonId!, categoryId!, matchDay!),
        enabled: !!seasonId && !!categoryId && !!matchDay,
        staleTime: 5 * 60 * 1000, // 5 minutes
        placeholderData: keepPreviousData,
        select: (data): ProductionsSearchResult => {
            // Separate CST productions from regular productions
            const productions = data.filter(
                (p: ProductionDropdownType) => !(p.isCstProduction && p.hasProductionRelation)
            );
            const cstProductions = data.filter(
                (p: ProductionDropdownType) => p.isCstProduction && p.hasProductionRelation
            );

            return {productions, cstProductions, raw: data};
        },
    });
};

/**
 * Fetch severe incidents for a production
 * Only used for PM reports
 */
export const useSevereIncidents = (productionId: number | undefined, enabled: boolean = true) => {
    return useQuery({
        queryKey: incidentKeys.severe(productionId!),
        queryFn: () => fetchSevereIncidents(productionId!),
        enabled: enabled && !!productionId,
        staleTime: 2 * 60 * 1000, // 2 minutes - more volatile data
        retry: false, // Don't retry on 4xx errors (no severe incidents may exist)
    });
};

/**
 * Fetch report note settings for a specific report type
 * Settings change rarely, so longer stale time
 */
export const useReportNoteSettings = (reportType: ReportTypes | string | undefined) => {
    return useQuery({
        queryKey: settingsKeys.reportNote(reportType!),
        queryFn: () => fetchReportNoteSettings(reportType!),
        enabled: !!reportType,
        staleTime: 30 * 60 * 1000, // 30 minutes - settings change rarely
        retry: false, // Don't retry on 4xx errors (note settings may not exist)
    });
};

/**
 * Fetch my reports with infinite scroll / load more pagination
 * Uses useInfiniteQuery for efficient "load more" pattern
 */
export const PAGE_SIZE = 25;

export const useMyReportsInfinite = (filters: MyReportsFilters) => {
    return useInfiniteQuery({
        queryKey: reportKeys.myReports(filters),
        queryFn: ({pageParam}) => fetchMyReports(filters, pageParam),
        initialPageParam: 0,
        getNextPageParam: (lastPage, allPages) => {
            const totalLoaded = allPages.length * PAGE_SIZE;
            if (totalLoaded < lastPage.total) {
                return allPages.length;
            }
            return undefined;
        },
        staleTime: 2 * 60 * 1000, // 2 minutes - reports list changes more frequently
    });
};

/**
 * Fetch my report IDs for role-based redirects
 * Used by Redirector for DIRECTOR, PARTNER, EMPTY_USER_MASK, DL roles
 */
export const useMyReportIdsQuery = (filters: ReportsFilterAndSort, enabled: boolean = true) => {
    return useQuery({
        queryKey: reportKeys.myReportIds(filters),
        queryFn: () => fetchMyReportIds(filters),
        enabled,
        staleTime: 2 * 60 * 1000, // 2 minutes
        retry: false,
    });
};

// =============================================================================
// Prefetching Hooks
// =============================================================================

interface PrefetchItem {
    productionId: number;
    reportType: string;
    priority: number;
}

/**
 * Prefetch hook for all reports of a match day.
 * Loads reports in the background once productions are available.
 *
 * Strategy:
 * - Current production has priority (loaded first)
 * - 100ms throttling between requests prevents server overload
 * - Cleanup on unmount or filter change
 *
 * @param productions - List of available productions
 * @param currentProductionId - ID of the currently selected production (priority)
 * @param enabled - Enables/disables prefetching
 */
export const usePrefetchProductionReports = (
    productions: ProductionDropdownType[],
    currentProductionId?: number,
    enabled: boolean = true
) => {
    const queryClient = useQueryClient();
    // Ref to track production IDs to prevent unnecessary re-runs
    const lastPrefetchedRef = useRef<string>('');

    useEffect(() => {
        if (!enabled || !productions.length) {
            return;
        }

        // Create a fingerprint of the current productions
        const productionFingerprint = productions.map(p => p.id).sort().join(',');

        // Skip if already prefetched for these productions
        if (lastPrefetchedRef.current === productionFingerprint) {
            return;
        }

        const timeoutIds: ReturnType<typeof setTimeout>[] = [];

        // Create queue with priorities: current production first
        const prefetchQueue: PrefetchItem[] = productions.flatMap(prod => {
            const reportTypes = prod.availableReports || prod.activeReports || [];
            return reportTypes.map(type => ({
                productionId: prod.id,
                reportType: type,
                // Priority 0 for current production, 1 for the rest
                priority: prod.id === currentProductionId ? 0 : 1
            }));
        }).sort((a, b) => a.priority - b.priority);

        // Prefetch with throttling (100ms between requests)
        prefetchQueue.forEach((item, index) => {
            const timeoutId = setTimeout(() => {
                queryClient.prefetchQuery({
                    queryKey: reportKeys.byProductionAndType(item.productionId, item.reportType),
                    queryFn: () => fetchReportByProductionAndType(item.productionId, item.reportType),
                    staleTime: 5 * 60 * 1000, // 5 minutes - consistent with useReportByProductionAndType
                });
            }, index * 100); // 100ms Throttling

            timeoutIds.push(timeoutId);
        });

        lastPrefetchedRef.current = productionFingerprint;

        // Cleanup: Cancel all pending prefetches
        return () => {
            timeoutIds.forEach(clearTimeout);
        };
    }, [productions, currentProductionId, enabled, queryClient]);
};
